import React, { FC, useCallback, useEffect, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { get } from 'lodash/fp';

interface SuggestDocumentHeightObserverProps {
    editorRef: (c: HTMLElement | null) => void;
    onHeightChange?: (height: number) => void;

    isEditing: boolean;
    isResizing: boolean;
}

export default <DecoratedProps extends SuggestDocumentHeightObserverProps>(DecoratedComponent: FC<DecoratedProps>) => {
    const suggestDocumentHeightObserver = ({ editorRef, ...props }: DecoratedProps) => {
        const { isEditing, isResizing, onHeightChange } = props;

        const editorElement = useRef<HTMLElement | null>(null);
        const wrappedEditorRef = useCallback(
            (c: HTMLElement | null) => {
                editorElement.current = c;
                editorRef?.(c);
            },
            [editorRef],
        );

        useEffect(() => {
            if (!isEditing || !onHeightChange || isResizing) return;

            const observer = new ResizeObserver((entries) => {
                const newEditorHeight = get([0, 'contentRect', 'height'], entries);
                onHeightChange(newEditorHeight);
            });

            // This only happens during a live reload - it's fine to ignore
            // (the next edit of the card will re-run this anyway)
            if (!editorElement.current) return;

            observer.observe(editorElement.current);

            return () => observer.disconnect();
        }, [isEditing, isResizing, onHeightChange]);

        return <DecoratedComponent {...(props as DecoratedProps)} editorRef={wrappedEditorRef} />;
    };

    return suggestDocumentHeightObserver;
};
